/**
 * Raw localStorage helpers for per-year favorite event IDs.
 *
 * Key format: companion_favorite_events_YEAR
 *
 * These keys live outside Redux Persist so they survive store resets
 * (e.g. hard-reload cache busting that wipes persist:* keys).
 */

export const FAVORITES_KEY_PREFIX = 'companion_favorite_events_'

export function favoritesStorageKey (year) {
  return `${FAVORITES_KEY_PREFIX}${year}`
}

/**
 * Returns the active convention year.
 * Reads from config.year if present; falls back to the current calendar year.
 */
export function getConventionYear (config) {
  return config?.year ?? new Date().getFullYear()
}

/**
 * Reads the stored favorite IDs array for the given year.
 * Returns an empty array when the key is absent or the value is unparseable.
 */
export function loadFavoriteIds (year) {
  try {
    const raw = localStorage.getItem(favoritesStorageKey(year))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/**
 * Persists the favorite IDs array for the given year to localStorage.
 */
export function saveFavoriteIds (year, ids) {
  try {
    localStorage.setItem(favoritesStorageKey(year), JSON.stringify(ids))
  } catch {
    // localStorage may be unavailable (private browsing quota etc.) — fail silently.
  }
}

/**
 * Removes the favorite IDs entry for the given year from localStorage.
 */
export function clearFavoriteIds (year) {
  try {
    localStorage.removeItem(favoritesStorageKey(year))
  } catch {
    // fail silently
  }
}

/**
 * Computes the union of two ID arrays (deduped, original types preserved).
 * Order: local IDs first, then any server-only additions.
 */
export function unionFavoriteIds (localIds, serverIds) {
  const seen = new Set(localIds)
  const union = [...localIds]
  for (const id of serverIds) {
    if (!seen.has(id)) {
      seen.add(id)
      union.push(id)
    }
  }
  return union
}
